/** Small presentational pieces shared across screens. */
import { Fragment } from "react";
import type { ReactNode } from "react";

/** A small pill, for a series, language or tag. */
export function Chip({
  children,
  tone = "quiet",
}: {
  children: ReactNode;
  tone?: "quiet" | "accent";
}) {
  const toneClass =
    tone === "accent"
      ? "border-gold/40 text-terra"
      : "border-hairline text-warm-gray";
  return (
    <span
      className={`inline-block rounded-full border px-2.5 py-0.5 text-[0.7rem] font-medium tracking-wide ${toneClass}`}
    >
      {children}
    </span>
  );
}

/** A 0–3 theme score as three dots. */
export function ScoreDots({ score, label }: { score: number; label: string }) {
  return (
    <span
      className="inline-flex items-center gap-1"
      aria-label={`${label}: ${score} of 3`}
      title={`${label}: ${score}/3`}
    >
      {[1, 2, 3].map((step) => (
        <span
          key={step}
          className={`h-1.5 w-1.5 rounded-full ${
            step <= score ? "bg-terra" : "bg-hairline"
          }`}
        />
      ))}
    </span>
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/gu, "\\$&");
}

/** Render `text`, wrapping any occurrence of a search term in a <mark>. */
export function Highlight({
  text,
  terms,
}: {
  text: string;
  terms: readonly string[];
}) {
  const usable = terms.filter((term) => term.length > 0);
  if (usable.length === 0) {
    return <>{text}</>;
  }
  const pattern = new RegExp(`(${usable.map(escapeRegExp).join("|")})`, "giu");
  const lower = new Set(usable.map((term) => term.toLowerCase()));
  const parts = text.split(pattern);
  return (
    <>
      {parts.map((part, index) =>
        lower.has(part.toLowerCase()) ? (
          <mark key={index}>{part}</mark>
        ) : (
          <Fragment key={index}>{part}</Fragment>
        ),
      )}
    </>
  );
}

/** A small terracotta loading ring; static under reduced motion. */
export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <span
      role="status"
      aria-label={label}
      className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-hairline border-t-terra motion-reduce:animate-none"
    />
  );
}
